import { useQuery } from "react-query";
import { useState, useRef, useEffect } from "react";

const fetchCountries = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_SERVER_URL}/data/location/readCountries`
  );
  const data = await response.json();

  if (data && data.status === "Status" && data.data) {
    return data.data;
  } else {
    throw new Error("Error fetching countries");
  }
};

const fetchStatesByCountry = async (countryId) => {
  if (!countryId) return [];
  
  const response = await fetch(
    `${process.env.REACT_APP_SERVER_URL}/data/location/readStatesByCountry/${countryId}`
  );
  const data = await response.json();

  if (data && data.status === "Status" && data.data) {
    return data.data;
  } else {
    throw new Error("Error fetching states");
  }
};

const fetchCitiesByCountryCode = async (countryCode) => {
  if (!countryCode) return [];
  
  const response = await fetch(
    `${process.env.REACT_APP_SERVER_URL}/data/location/readCitiesByCountryCode/${countryCode}`
  );
  const data = await response.json();

  if (data && data.status === "Status" && data.data) {
    return data.data;
  } else {
    throw new Error("Error fetching cities");
  }
};

const CountryStateCitySelector = ({ formData, handleInputChange }) => {
  // Fetch countries
  const { data: countries, error: countriesError } = useQuery("countries", fetchCountries);
  
  // Get the selected country data
  const selectedCountry = formData.country 
    ? countries?.find(country => country.name === formData.country)
    : null;
  
  // Fetch states by country ID
  const { data: statesByCountry, error: statesByCountryError } = useQuery(
    ["statesByCountry", selectedCountry?.id],
    () => fetchStatesByCountry(selectedCountry?.id),
    { enabled: !!selectedCountry?.id }
  );
  
  // Fetch cities by country code
  const { data: citiesByCountryCode, error: citiesByCountryCodeError } = useQuery(
    ["citiesByCountryCode", selectedCountry?.iso2],
    () => fetchCitiesByCountryCode(selectedCountry?.iso2),
    { enabled: !!selectedCountry?.iso2 }
  );

  // State for custom dropdowns
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  
  // Search terms for each dropdown
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  
  // Refs for click outside handling
  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const cityRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setIsCountryOpen(false);
      }
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setIsStateOpen(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target)) {
        setIsCityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (countriesError) {
    console.error("Error fetching countries:", countriesError);
  }

  if (statesByCountryError) {
    console.error("Error fetching states by country:", statesByCountryError);
  }

  if (citiesByCountryCodeError) {
    console.error("Error fetching cities by country code:", citiesByCountryCodeError);
  }

  // Get the selected state data
  const selectedState = formData.state 
    ? (statesByCountry || []).find(state => state.name === formData.state)
    : null;
  
  // Filter cities by state if a state is selected
  let filteredCities = [];
  if (citiesByCountryCode && selectedState) {
    // Filter cities by state name or state code
    filteredCities = citiesByCountryCode.filter(city => 
      city.state_name === selectedState.name || 
      city.state_code === selectedState.state_code
    );
  } else if (citiesByCountryCode) {
    // If no state is selected, use all cities for the country
    filteredCities = citiesByCountryCode;
  }

  // Filter options based on search terms
  const filteredCountries = countries?.filter(country => 
    country.name.toLowerCase().includes(countrySearchTerm.toLowerCase())
  ) || [];

  const filteredStates = statesByCountry?.filter(state => 
    state.name.toLowerCase().includes(stateSearchTerm.toLowerCase())
  ) || [];

  const filteredCitiesList = filteredCities.filter(city => 
    city.name.toLowerCase().includes(citySearchTerm.toLowerCase())
  );

  // Handler for country selection
  const handleCountrySelect = (countryName) => {
    // Update form data
    handleInputChange({ target: { name: "country", value: countryName } });
    handleInputChange({ target: { name: "state", value: "" } });
    handleInputChange({ target: { name: "city", value: "" } });
    
    // Close dropdown and reset search
    setIsCountryOpen(false);
    setCountrySearchTerm('');
    
    // Force state update
    setTimeout(() => {
      setIsCountryOpen(false);
    }, 0);
  };

  // Handler for state selection
  const handleStateSelect = (stateName) => {
    // Update form data
    handleInputChange({ target: { name: "state", value: stateName } });
    handleInputChange({ target: { name: "city", value: "" } });
    
    // Close dropdown and reset search
    setIsStateOpen(false);
    setStateSearchTerm('');
    
    // Force state update
    setTimeout(() => {
      setIsStateOpen(false);
    }, 0);
  };

  // Handler for city selection
  const handleCitySelect = (cityName) => {
    // Update form data
    handleInputChange({ target: { name: "city", value: cityName } });
    
    // Close dropdown and reset search
    setIsCityOpen(false);
    setCitySearchTerm('');
    
    // Force state update
    setTimeout(() => {
      setIsCityOpen(false);
    }, 0);
  };

  // Toggle functions for dropdowns
  const toggleCountryDropdown = () => {
    setIsCountryOpen(!isCountryOpen);
    // Close other dropdowns
    setIsStateOpen(false);
    setIsCityOpen(false);
  };

  const toggleStateDropdown = () => {
    setIsStateOpen(!isStateOpen);
    // Close other dropdowns
    setIsCountryOpen(false);
    setIsCityOpen(false);
  };

  const toggleCityDropdown = () => {
    setIsCityOpen(!isCityOpen);
    // Close other dropdowns
    setIsCountryOpen(false);
    setIsStateOpen(false);
  };

  return (
    <>
      <label>
        Country
        <div className="custom-select-container" ref={countryRef}>
          <button 
            type="button"
            className="custom-select-button"
            onClick={toggleCountryDropdown}
            disabled={!countries || countries.length === 0}
          >
            {formData.country || "Select Country"}
          </button>
          {isCountryOpen && (
            <div className="custom-select-dropdown show">
              <div className="custom-select-search">
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearchTerm}
                  onChange={(e) => setCountrySearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <div
                    key={country.id}
                    className={`custom-select-option ${formData.country === country.name ? 'selected' : ''}`}
                    onClick={() => handleCountrySelect(country.name)}
                  >
                    {country.name}
                  </div>
                ))
              ) : (
                <div className="custom-select-option">No countries available</div>
              )}
            </div>
          )}
        </div>
      </label>
      
      <label>
        State/Province
        <div className="custom-select-container" ref={stateRef}>
          <button 
            type="button"
            className="custom-select-button"
            onClick={toggleStateDropdown}
            disabled={!selectedCountry || !statesByCountry || statesByCountry.length === 0}
          >
            {formData.state || "Select State/Province"}
          </button>
          {isStateOpen && (
            <div className="custom-select-dropdown show">
              <div className="custom-select-search">
                <input
                  type="text"
                  placeholder="Search states..."
                  value={stateSearchTerm}
                  onChange={(e) => setStateSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {filteredStates.length > 0 ? (
                filteredStates.map((state) => (
                  <div
                    key={state.id}
                    className={`custom-select-option ${formData.state === state.name ? 'selected' : ''}`}
                    onClick={() => handleStateSelect(state.name)}
                  >
                    {state.name}
                  </div>
                ))
              ) : (
                <div className="custom-select-option">No states available</div>
              )}
            </div>
          )}
        </div>
      </label>
      
      <label>
        City
        <div className="custom-select-container" ref={cityRef}>
          <button 
            type="button"
            className="custom-select-button"
            onClick={toggleCityDropdown}
            disabled={!selectedCountry || filteredCities.length === 0}
          >
            {formData.city || "Select City"}
          </button>
          {isCityOpen && (
            <div className="custom-select-dropdown show">
              <div className="custom-select-search">
                <input
                  type="text"
                  placeholder="Search cities..."
                  value={citySearchTerm}
                  onChange={(e) => setCitySearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {filteredCitiesList.length > 0 ? (
                filteredCitiesList.map((city) => (
                  <div
                    key={city.id}
                    className={`custom-select-option ${formData.city === city.name ? 'selected' : ''}`}
                    onClick={() => handleCitySelect(city.name)}
                  >
                    {city.name}
                  </div>
                ))
              ) : (
                <div className="custom-select-option">
                  {selectedCountry?.capital ? `No cities available. Capital: ${selectedCountry.capital}` : 'No city data available'}
                </div>
              )}
            </div>
          )}
        </div>
      </label>
    </>
  );
};

export default CountryStateCitySelector; 